/**
 * Atelier theme — box-builder.js
 * Scarf gift-box builder. `state` is the single source of truth: every render reads it, never the DOM.
 * Data comes from one JSON script tag written by sections/box-builder.liquid (no per-product fetches).
 */
(function () {
  'use strict';

  var CHUNK = 12;
  var instances = new WeakMap();

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  function fmt(str, vars) {
    return str.replace(/\[(\w+)\]/g, function (m, key) {
      return vars && key in vars ? vars[key] : m;
    });
  }

  function build(root) {
    var data = JSON.parse(root.querySelector('[data-bb-data]').textContent);
    var t = data.i18n;
    var $ = function (name) { return root.querySelector('[data-bb-' + name + ']'); };
    var slotsEl = $('slots'), gridEl = $('grid'), moreEl = $('more'), msgEl = $('msg'), liveEl = $('live');
    var countEl = $('count'), totalEl = $('total'), hintEl = $('hint'), submitEl = $('submit');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var sizes = String(data.sizes).split(',').map(Number).filter(function (n) { return n >= 1 && n <= 8; });
    if (!sizes.length) sizes = [3];

    var products = data.products, byId = {}, byVariant = {};
    products.forEach(function (p) {
      byId[p.id] = p;
      p.v.forEach(function (v) { v.p = p; byVariant[v.id] = v; });
    });

    var state = { size: sizes[0], slots: [], pick: {}, busy: false };
    var shown = 0, raf = 0, ctrl = null, io = null, builtSize = 0;
    var cards = {};

    /* ---------- helpers ---------- */

    function emptySlots(n) {
      var out = [];
      for (var i = 0; i < n; i++) out.push(null);
      return out;
    }

    function fit(ids, n) {
      var kept = ids.filter(Boolean).slice(0, n);
      return kept.concat(emptySlots(n - kept.length));
    }

    function money(cents) {
      var format = data.moneyFormat.replace(/<[^>]*>/g, '');
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
        var whole = /no_decimals/.test(key);
        var comma = /comma/.test(key) || (/space/.test(key) && !/period/.test(key));
        var group = /apostrophe/.test(key) ? "'" : /space/.test(key) ? ' ' : comma ? '.' : ',';
        var parts = (whole ? String(Math.round(cents / 100)) : (cents / 100).toFixed(2)).split('.');
        return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, group) + (parts[1] ? (comma ? ',' : '.') + parts[1] : '');
      });
    }

    function variantOf(p) {
      var picked = byVariant[state.pick[p.id]];
      if (picked) return picked;
      for (var i = 0; i < p.v.length; i++) if (p.v[i].a) return p.v[i];
      return p.v[0];
    }

    function label(v) { return v.p.v.length > 1 ? v.p.t + ' – ' + v.t : v.p.t; }
    function slotImage(v) { return v.p.f || v.s || v.p.s; }
    function cardImage(v) { return v.u || v.p.u; }
    function filled() { return state.slots.filter(Boolean).length; }
    function countOf(p) {
      return state.slots.filter(function (id) { return id && byVariant[id].p === p; }).length;
    }
    function ready() {
      return filled() === state.size && state.slots.every(function (id) { return byVariant[id].a; });
    }

    function announce(text) { liveEl.textContent = text; }

    function setMsg(text) { msgEl.textContent = text || ''; }

    /* ---------- persistence ---------- */

    function save() {
      try { sessionStorage.setItem(data.key, JSON.stringify({ size: state.size, slots: state.slots })); } catch (e) { /* storage unavailable */ }
    }

    function restore() {
      var saved;
      try { saved = JSON.parse(sessionStorage.getItem(data.key)); } catch (e) { saved = null; }
      state.slots = emptySlots(state.size);
      if (!saved || sizes.indexOf(saved.size) < 0 || !Array.isArray(saved.slots)) return;
      var seen = {};
      var valid = saved.slots.map(function (id) {
        var v = byVariant[id];
        if (!v || !v.a) return null;
        if (!data.dup && seen[v.p.id]) return null;
        seen[v.p.id] = true;
        return id;
      });
      state.size = saved.size;
      state.slots = fit(valid, state.size);
    }

    /* ---------- slots ---------- */

    function layoutFor(n) {
      var cfg;
      try { cfg = JSON.parse(data.slotConfig)[n]; } catch (e) { /* even layout */ }
      if (Array.isArray(cfg) && cfg.length === n) return cfg;
      var w = 90 / n, out = [];
      for (var i = 0; i < n; i++) out.push({ x: 5 + i * w, y: 12, w: w - 2, h: 76, r: 0, z: 1 });
      return out;
    }

    function buildSlots() {
      slotsEl.textContent = '';
      layoutFor(state.size).forEach(function (s) {
        var li = el('li', 'bb-slot is-empty');
        li.style.cssText = 'left:' + s.x + '%;top:' + s.y + '%;width:' + s.w + '%;height:' + s.h + '%;transform:rotate(' + (s.r || 0) + 'deg);z-index:' + (s.z || 1);
        li.setAttribute('role', 'listitem');
        slotsEl.appendChild(li);
      });
      builtSize = state.size;
    }

    function paintSlot(li, index, id) {
      var old = li.querySelector('.bb-s-in');
      if (old) {
        if (reduceMotion.matches) {
          old.remove();
        } else {
          old.classList.add('is-out');
          old.setAttribute('aria-hidden', 'true');
          setTimeout(function () { old.remove(); }, 260);
        }
      }
      li.dataset.v = id || '';
      li.classList.toggle('is-empty', !id);
      var inner = el('div', 'bb-s-in' + (id && !reduceMotion.matches ? ' is-in' : ''));
      if (id) {
        var v = byVariant[id];
        var img = el('img', 'bb-s-img');
        var src = slotImage(v);
        if (src) img.src = src; else inner.classList.add('is-broken');
        img.alt = '';
        img.decoding = 'async';
        img.addEventListener('error', function () { inner.classList.add('is-broken'); });
        var btn = el('button', 'bb-s-x');
        btn.type = 'button';
        btn.dataset.act = 'unslot';
        btn.dataset.i = index;
        btn.setAttribute('aria-label', fmt(t.slot_filled, { n: index + 1, title: label(v) }));
        btn.appendChild(el('span', '', '×'));
        inner.appendChild(img);
        inner.appendChild(btn);
        setTimeout(function () { inner.classList.remove('is-in'); }, 260);
      } else {
        inner.appendChild(el('span', 'bb-s-plus'));
        inner.appendChild(el('span', 'visually-hidden', fmt(t.slot_empty, { n: index + 1 })));
      }
      li.appendChild(inner);
    }

    function renderSlots() {
      if (builtSize !== state.size) buildSlots();
      var items = slotsEl.children;
      for (var i = 0; i < items.length; i++) {
        var id = state.slots[i] || '';
        if (String(items[i].dataset.v || '') !== String(id) || !items[i].firstChild) paintSlot(items[i], i, id);
      }
    }

    /* ---------- product cards ---------- */

    function srcset(url) {
      return [240, 360, 480].map(function (w) { return url.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w'; }).join(', ');
    }

    function buildCard(p) {
      var li = el('li', 'bb-card');
      li.dataset.pid = p.id;
      var media = el('div', 'bb-c-media');
      var img = el('img');
      img.sizes = '(min-width: 990px) 20vw, 45vw';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = '';
      var count = el('span', 'bb-c-count'), price = el('p', 'bb-c-price'), select = null;
      media.appendChild(img);
      media.appendChild(count);
      li.appendChild(media);
      li.appendChild(el('h3', 'bb-c-title', p.t));
      li.appendChild(price);
      if (p.v.length > 1) {
        select = el('select', 'bb-c-select');
        select.setAttribute('aria-label', p.t);
        p.v.forEach(function (v) {
          var o = el('option', '', v.a ? v.t : fmt(t.variant_sold_out, { title: v.t }));
          o.value = v.id;
          o.disabled = !v.a;
          select.appendChild(o);
        });
        li.appendChild(select);
      }
      var row = el('div', 'bb-c-row');
      var add = el('button', 'button button--primary bb-c-add', t.add);
      add.type = 'button';
      add.dataset.act = 'add';
      var rm = el('button', 'bb-c-rm');
      rm.type = 'button';
      rm.dataset.act = 'remove';
      rm.hidden = true;
      rm.appendChild(el('span', '', '×'));
      row.appendChild(add);
      row.appendChild(rm);
      li.appendChild(row);
      cards[p.id] = { li: li, img: img, price: price, count: count, select: select, add: add, rm: rm, src: '' };
      return li;
    }

    function showMore() {
      var frag = document.createDocumentFragment();
      products.slice(shown, shown + CHUNK).forEach(function (p) { frag.appendChild(buildCard(p)); });
      shown = Math.min(shown + CHUNK, products.length);
      gridEl.appendChild(frag);
      moreEl.hidden = shown >= products.length;
      if (moreEl.hidden && io) io.disconnect();
      schedule();
      if (io && !moreEl.hidden) requestAnimationFrame(function () { io.unobserve(moreEl); io.observe(moreEl); });
    }

    function renderCards() {
      var full = filled() >= state.size;
      products.slice(0, shown).forEach(function (p) {
        var c = cards[p.id], v = variantOf(p), n = countOf(p);
        var src = cardImage(v);
        if (src && c.src !== src) {
          c.src = src;
          c.img.srcset = srcset(src);
          c.img.src = src;
        }
        c.price.textContent = money(v.c);
        if (c.select && String(c.select.value) !== String(v.id)) c.select.value = v.id;
        var blocked = !v.a || full || (!data.dup && n > 0);
        c.add.disabled = blocked;
        c.add.textContent = !v.a ? t.sold_out : (!data.dup && n > 0 ? t.already_in_box : t.add);
        c.rm.hidden = n === 0;
        c.rm.setAttribute('aria-label', fmt(t.remove_item, { title: label(v) }));
        c.count.textContent = n > 0 ? fmt(t.in_box, { n: n }) : '';
        c.li.classList.toggle('is-selected', n > 0);
      });
    }

    /* ---------- summary ---------- */

    function renderSummary() {
      var f = filled(), total = 0;
      state.slots.forEach(function (id) { if (id) total += byVariant[id].c; });
      countEl.textContent = fmt(t.progress, { filled: f, size: state.size });
      totalEl.textContent = money(total);
      var open = state.slots.some(function (id) { return id && !byVariant[id].a; });
      hintEl.textContent = f < state.size ? fmt(t.choose_more, { n: state.size - f }) : open ? t.remove_sold_out : t.ready;
      submitEl.disabled = state.busy || !ready();
      submitEl.setAttribute('aria-busy', state.busy ? 'true' : 'false');
      submitEl.textContent = state.busy ? t.adding : t.add_box;
      var radios = root.querySelectorAll('[data-bb-sizes] input');
      for (var i = 0; i < radios.length; i++) radios[i].checked = Number(radios[i].value) === state.size;
    }

    function render() {
      raf = 0;
      renderSlots();
      renderCards();
      renderSummary();
      save();
    }

    function schedule() {
      if (!raf) raf = requestAnimationFrame(render);
    }

    /* ---------- actions ---------- */

    function addProduct(id) {
      var p = byId[id], v = p && variantOf(p), i = state.slots.indexOf(null);
      if (!v || !v.a || i < 0 || state.busy || (!data.dup && countOf(p))) return;
      state.slots[i] = v.id;
      announce(fmt(t.announce_added, { title: label(v), n: i + 1 }));
      setMsg('');
      schedule();
    }

    function clearSlot(i) {
      var id = state.slots[i];
      if (!id || state.busy) return;
      state.slots[i] = null;
      announce(fmt(t.announce_removed, { title: label(byVariant[id]), n: i + 1 }));
      setMsg('');
      schedule();
    }

    function removeProduct(id) {
      for (var i = state.slots.length - 1; i >= 0; i--) {
        if (state.slots[i] && byVariant[state.slots[i]].p.id === id) return clearSlot(i);
      }
    }

    function setSize(n) {
      var ids = state.slots.filter(Boolean);
      if (state.busy || n === state.size) return schedule();
      if (ids.length > n && !window.confirm(fmt(t.confirm_shrink, { n: ids.length - n }))) return schedule();
      state.size = n;
      state.slots = fit(ids, n);
      schedule();
    }

    function reset() {
      state.slots = emptySlots(state.size);
      try { sessionStorage.removeItem(data.key); } catch (e) { /* storage unavailable */ }
    }

    function refreshAvailability() {
      var seen = {};
      var jobs = state.slots.filter(Boolean).map(function (id) {
        var p = byVariant[id].p;
        if (seen[p.id]) return null;
        seen[p.id] = true;
        return fetch(data.root + 'products/' + p.h + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (!j) return;
            j.variants.forEach(function (v) { if (byVariant[v.id]) byVariant[v.id].a = v.available; });
          })
          .catch(function () { /* keep last known availability */ });
      }).filter(Boolean);
      return Promise.all(jobs);
    }

    function uid() {
      return window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }

    function submit() {
      if (state.busy || !ready()) return;
      state.busy = true;
      ctrl = new AbortController();
      setMsg('');
      schedule();
      var props = { _box_id: uid() };
      props[t.property_size] = String(state.size);
      props[t.property_name] = data.boxName;
      var counts = {};
      state.slots.forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });
      var items = Object.keys(counts).map(function (id) { return { id: Number(id), quantity: counts[id], properties: props }; });
      // Same mechanism as the theme's product-form: ask for the drawer/notification sections in the add response, then let it render them.
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var payload = { items: items };
      if (cart) {
        payload.sections = cart.getSectionsToRender().map(function (x) { return x.id; }).join(',');
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }
      fetch(data.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: ctrl.signal
      })
        .then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, status: r.status, body: j }; });
        })
        .then(function (res) {
          if (!res.ok) throw res;
          if (!cart) {
            window.location = data.cartUrl;
            return;
          }
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'box-builder', cartData: res.body });
          reset();
          setMsg(t.added);
          cart.renderContents(res.body);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (!err || typeof err.status !== 'number') return setMsg(t.err_network);
          var text = err.body && (err.body.description || err.body.message) || '';
          if (err.status !== 422) return setMsg(fmt(t.err_generic, { message: text }));
          return refreshAvailability().then(function () {
            var gone = state.slots.filter(function (id) { return id && !byVariant[id].a; }).map(function (id) { return label(byVariant[id]); });
            setMsg(gone.length ? fmt(t.err_sold_out, { title: gone.join(', ') }) : fmt(t.err_generic, { message: text }));
          });
        })
        .then(function () {
          state.busy = false;
          ctrl = null;
          // Same as the theme's product-form: the drawer must not stay flagged empty once it has been filled.
          if (cart) cart.classList.remove('is-empty');
          schedule();
        });
    }

    /* ---------- events (delegated) ---------- */

    function onClick(event) {
      var target = event.target.closest('[data-act]');
      if (!target || !root.contains(target)) return;
      var card = target.closest('.bb-card');
      var pid = card ? Number(card.dataset.pid) : 0;
      var act = target.dataset.act;
      if (act === 'add') addProduct(pid);
      else if (act === 'remove') removeProduct(pid);
      else if (act === 'unslot') clearSlot(Number(target.dataset.i));
      else if (act === 'more') showMore();
      else if (act === 'submit') submit();
    }

    function onChange(event) {
      var target = event.target;
      if (target.name === 'bb-size') return setSize(Number(target.value));
      if (target.classList.contains('bb-c-select')) {
        state.pick[Number(target.closest('.bb-card').dataset.pid)] = Number(target.value);
        schedule();
      }
    }

    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);

    /* ---------- init ---------- */

    if (!products.length) setMsg(t.empty_collection);
    else if (!data.dup && products.length < sizes[sizes.length - 1]) setMsg(t.not_enough);
    restore();
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) { if (entries[0].isIntersecting) showMore(); }, { rootMargin: '400px' });
    }
    showMore();
    root.classList.add('is-ready');

    return function destroy() {
      if (ctrl) ctrl.abort();
      if (io) io.disconnect();
      cancelAnimationFrame(raf);
      root.removeEventListener('click', onClick);
      root.removeEventListener('change', onChange);
    };
  }

  function init(root) {
    if (!instances.has(root)) instances.set(root, build(root));
  }

  document.querySelectorAll('[data-bb]').forEach(init);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-bb]');
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-bb]');
    var destroy = root && instances.get(root);
    if (destroy) {
      destroy();
      instances.delete(root);
    }
  });
})();
